"use client";
// app/components/Sidebar.tsx
import {
  LayoutDashboard,
  BookOpenCheck,
  BookOpenText,
  ListChecks,
  ClipboardList,
  BarChart,
  Activity,
  Search,
  ChevronDown,
  Layers,
  Circle,
  CircleArrowUp,
  MoreHorizontal,
  Instagram,
  Youtube,
  Newspaper,
  GraduationCap,
  Library,
  SpellCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useMemo, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { usePurchasePromo } from '@/hooks/usePurchasePromo';
import Logo from '@/assets/images/fusha.png';

function TikTokIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

const menuItems = [
  {
    title: 'Study',
    items: [
      { href: '/app', icon: LayoutDashboard, text: 'Beranda' },
      {
        text: 'Buku',
        icon: BookOpenText,
        subItems: [{ href: '/books', text: 'Semua Buku', icon: Library }],
      },
      { href: '/lesson', icon: BookOpenCheck, text: 'Pelajaran' },
      // { href: '/chat', icon: Database, text: 'AI Tutor' },
      { href: '/question-bank', icon: ListChecks, text: 'Latihan Soal' },
      { href: '/flashcard', icon: Layers, text: 'Flashcard' },
      { href: '/vocabulary-practice', icon: SpellCheck, text: 'Latihan Kosakata' },
    ],
  },
  {
    title: 'Stats',
    items: [
      { href: '/exam', icon: ClipboardList, text: 'Ujian' },
      { href: '/track', icon: Activity, text: 'Progress' },
    ],
  },
  {
    title: 'Lainnya',
    items: [
      {
        text: 'Lainnya',
        icon: MoreHorizontal,
        subItems: [
          { href: 'https://annajmacademy.com', text: 'Cari Tutor', icon: GraduationCap },
          { href: 'https://annajmacademy.com/blog', text: 'Blog', icon: Newspaper },
          { href: 'https://instagram.com/annajmacademy', text: 'Instagram', icon: Instagram }
        ],
      },
    ],
  },
];

const SidebarMenuContent = ({ name, email }: { name: string; email: string }) => {
  const pathname = usePathname();
  const { isSubscribed } = useSubscriptionStatus();
  const { promo } = usePurchasePromo();
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsAndroid(Capacitor.getPlatform() === 'android');
    }
  }, []);

  const promoDiscountPercent = useMemo(() => {
    if (!promo.active) return 0;
    const percents = Object.values(promo.tiers)
      .filter((tier) => tier.isDiscount)
      .map((tier) => tier.discountPercent);
    return percents.length ? Math.max(...percents) : 0;
  }, [promo]);

  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSubmenu = (key: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredMenuItems = menuItems
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const q = searchQuery.toLowerCase();
        if (item.text.toLowerCase().includes(q)) return true;
        return item.subItems?.some((sub) => sub.text.toLowerCase().includes(q)) ?? false;
      }),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <aside className="flex h-full min-h-0 w-64 flex-shrink-0 flex-col overflow-hidden bg-brand-background">
      <div className="relative min-h-0 min-w-0 flex-1">
        <div
          className="absolute inset-0 overflow-y-auto overscroll-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="h-16 flex items-center mx-6 border-b border-dashed border-brand-border">
            <Link href="/app" className="inline-flex shrink-0 transition opacity-90 hover:opacity-100">
              <img
                src={Logo.src}
                alt="Fusha"
                className="w-[200px] h-[33px] min-[1280px]:max-[1512px]:mt-3"
              />
            </Link>
            {/* <h1 className="text-xl font-bold text-gray-800">Annajm Learn</h1> */}
          </div>
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cari menu..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <nav className="px-4 pb-10 space-y-2">
            {filteredMenuItems.map((section) => (
              <div key={section.title}>
                <h2 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">
                  {section.title}
                </h2>
                {section.items.map((item) =>
                  item.subItems ? (
                    <div key={item.text} className="rounded-md">
                      <button
                        type="button"
                        onClick={() => toggleSubmenu(item.text)}
                        className={`flex rounded-md items-center text-gray-700 w-full px-2 py-2 text-sm font-medium`}
                      >
                        <span
                          className={`mr-3 flex h-6 w-6 items-center justify-center rounded-md border border-brand-border bg-white`}
                        >
                          <item.icon className="h-4 w-4" />
                        </span>
                        {item.text}
                        <ChevronDown
                          className={`h-4 w-4 ml-auto transform transition-transform ${openSubmenus[item.text] ? 'rotate-180' : ''
                            }`}
                        />
                      </button>
                      {openSubmenus[item.text] && (
                        <div className="ml-4">
                          {item.subItems.map((subItem) => {
                            const isExternal = subItem.href.startsWith('http');
                            const isActive = !isExternal && pathname === subItem.href;
                            const SubIcon = subItem.icon as LucideIcon | undefined;
                            const rowClass =
                              `flex items-center mt-1 py-2 pl-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100` +
                              (isActive ? ' border bg-brand-purple border-brand-purpleBorder' : '');

                            const label = (
                              <>
                                <span
                                  className={
                                    `mr-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-brand-border bg-white` +
                                    (isActive ? ' text-brand-purpleText' : '')
                                  }
                                >
                                  {SubIcon ? (
                                    <SubIcon className="h-4 w-4" />
                                  ) : (
                                    <Circle className="h-[7px] w-[7px]" />
                                  )}
                                </span>
                                <span className="min-w-0 flex-1 truncate">{subItem.text}</span>
                              </>
                            );

                            return isExternal ? (
                              <a
                                key={subItem.href}
                                href={subItem.href}
                                target="_blank"
                                rel="noreferrer"
                                className={rowClass}
                              >
                                {label}
                              </a>
                            ) : (
                              <Link key={subItem.href} href={subItem.href} className={rowClass}>
                                {label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item.href}
                      href={item.href || '#'}
                      className={
                        `flex items-center px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100` +
                        (pathname === item.href
                          ? ' border bg-brand-purple border-brand-purpleBorder'
                          : '')
                      }
                    >
                      <span
                        className={
                          `mr-3 flex h-6 w-6 items-center justify-center rounded-md border border-brand-border bg-white` +
                          (pathname === item.href ? ' text-brand-purpleText' : '')
                        }
                      >
                        {item.icon && <item.icon className="h-4 w-4" />}
                      </span>
                      {item.text}
                    </Link>
                  )
                )}
              </div>
            ))}
          </nav>
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-11 bg-gradient-to-t from-brand-background via-brand-background/85 to-transparent"
        />
      </div>
      <div className="shrink-0 border-t border-dashed border-brand-border bg-brand-background pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {!isSubscribed && !isAndroid && (
          <div className="mx-4 space-y-2 pt-2">
            <Link
              href="/purchase"
              className="flex min-w-0 items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-brand-border bg-white">
                <CircleArrowUp className="h-4 w-4 text-brand-purpleText" />
              </span>
              <span className="min-w-0 flex-1 truncate">Upgrade</span>
              {promo.active && promoDiscountPercent > 0 && (
                <span className="shrink-0 rounded-full bg-purple-600 px-1.5 py-0.5 text-[9px] font-semibold leading-tight text-white">
                  {promoDiscountPercent}% OFF
                </span>
              )}
            </Link>
          </div>
        )}
        <div className="p-4 pt-2">
          <Link href="/profile">
            <div className="flex items-center rounded-lg border border-purple-100 bg-purple-50 p-2">

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-purple-200 bg-purple-100">
                <span className="font-bold text-purple-700">{name?.substr(0, 1).toUpperCase()}</span>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{name}</p>
                {/* <p className="text-xs text-gray-500 truncate">{email}</p> */}
              </div>
            </div>
          </Link>
          {/* <ChevronDown className="h-5 w-5 ml-auto text-gray-500" /> */}
        </div>
      </div>
    </aside>
  );
};

export default SidebarMenuContent;
